"use client";

import Grid from "@mui/material/Grid2";
import { Formik } from "formik";

import MDBox from "@/components/atoms/MDBox";
import RecaptchaBadge from "@/components/atoms/RecaptchaBadge";
import PWAInstallBanner from "@/components/organisms/PWAInstallBanner";
import LeftContent from "@/components/screens/Login/components/LeftContent";
import LoginForm from "@/components/screens/Login/components/LoginForm";
import {
  buildLoginFormInitialData,
  buildLoginFormSchema,
} from "@/components/screens/Login/form-data";
import { RECAPTCHA_ACTIONS } from "@/constants/recaptcha.constants";
import useAuth from "@/contexts/auth-context";
import useToast from "@/contexts/toast-context";
import useLoading from "@/hooks/use-loading";
import { useRecaptchaVerification } from "@/hooks/useRecaptcha";
import { ILogin } from "@/interfaces/auth.interfaces";
import { LoginMessages } from "@/messages/login/login.messages";

type LoginProps = {
  messages: LoginMessages;
};

type RecaptchaProps = {
  handleSubmit: (formData: ILogin, _unused?: unknown) => Promise<void>;
  messages: LoginMessages;
  isLoading: boolean;
};

function Login({ messages }: LoginProps) {
  const { login, isLoggingIn: isLoadingLogin } = useAuth();
  const { showToast } = useToast();
  const { startLoading, endLoading, isLoading: isLoadingSubmit } = useLoading();
  const { executeAndVerify, isLoading: isRecaptchaLoading } = useRecaptchaVerification();

  const handleSubmit = async (_formData: ILogin): Promise<void> => {
    startLoading();

    try {
      // Executa e verifica reCAPTCHA
      const isRecaptchaValid = await executeAndVerify(RECAPTCHA_ACTIONS.LOGIN);

      if (!isRecaptchaValid) {
        // Mensagem de erro já foi exibida pelo hook
        endLoading();
        return;
      }

      // Procede com o login se reCAPTCHA foi validado
      const formData = { ..._formData, login: _formData.email };

      if (typeof login === "function") {
        login({ formData });
      } else {
        showToast({ text: "Erro interno: função de login não disponível", type: "error" });
      }
    } catch (error) {
      showToast({
        text: "Erro durante o processo de login. Tente novamente.",
        type: "error",
      });
    } finally {
      endLoading();
    }
  };

  const isLoading = isLoadingLogin || isLoadingSubmit || isRecaptchaLoading;

  return <RecaptchaWrapper handleSubmit={handleSubmit} messages={messages} isLoading={isLoading} />;
}

const RecaptchaWrapper: React.FC<RecaptchaProps> = ({ handleSubmit, messages, isLoading }) => {
  return (
    <>
      <Formik
        initialValues={buildLoginFormInitialData()}
        validateOnChange={false}
        onSubmit={(formData: ILogin) => handleSubmit(formData)}
        validationSchema={buildLoginFormSchema(messages)}
      >
        <Grid container sx={{ height: "var(--full-vh)", width: "100%" }}>
          <Grid display={{ xs: "none", md: "grid" }} size={{ lg: 7.5 }}>
            <LeftContent />
          </Grid>
          <Grid
            size={{ xs: 12, lg: 4.5 }}
            sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
          >
            <MDBox
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
              height={"100%"}
              width={"100%"}
              bgColor="primary"
              position={"relative"}
              zIndex={3}
            >
              <LoginForm isLoading={isLoading} messages={messages} />
            </MDBox>
          </Grid>
        </Grid>
      </Formik>
      <RecaptchaBadge show={true} />
      <PWAInstallBanner />
    </>
  );
};

export default Login;
